import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { Box, Input as MuiInput, Tabs, Tab } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { colors } from "../../theme/colors";
import SurgeonTab from "./tabs/SurgeonTab";
import NurseTab from "./tabs/NurseTab";
import AdminTab from "./tabs/AdminTab";
import CourierTab from "./tabs/CourierTab";
import { Button } from "../../components";
import style from "./user.module.css";
import { useGetUserRolesQuery } from "../../services/api";
import { DEFAULT_COURIER_ROLE_ID, DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID, DEFAULT_LABORATORY_ADMIN_ROLE_ID, DEFAULT_NURSE_ROLE_ID } from "../../utils/constants";

/** Tab identifiers for the user list role filters. */
type UserTabKey = "NURSES" | "SURGEONS" | "ADMINS" | "COURIERS";

/** Shape of the router location state used by UserList. */
type UsersLocationState = {
  tab?: string;
};

const USER_TAB_LABELS: Record<UserTabKey, string> = {
  NURSES: "Nurses",
  SURGEONS: "Surgeons",
  ADMINS: "Admins",
  COURIERS: "Couriers",
};

const USER_TAB_LABELS_SINGULAR: Record<UserTabKey, string> = {
  NURSES: "Nurse",
  SURGEONS: "Surgeon",
  ADMINS: "Admin",
  COURIERS: "Courier",
};
/**
 * Safely extracts the UserList location state from an unknown value.
 *
 * Args:
 *   state: Unknown location state to validate.
 *
 * Returns:
 *   UsersLocationState | null: Parsed state when valid; otherwise null.
 */
const getUsersLocationState = (state: unknown): UsersLocationState | null => {
  if (!state || typeof state !== "object") {
    return null;
  }
  const { tab } = state as { tab?: unknown };
  return typeof tab === "string" ? { tab } : null;
};

/**
 * Renders the user list with role tabs, search, and add navigation.
 *
 * Returns:
 *   JSX.Element: User list page.
 */
const UserList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);
  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });
  const location = useLocation();
  // Location state drives lab vs health-org context; guard to avoid unsafe casts.
  const locationState = getUsersLocationState(location.state);
  const isLaboratoryContext =
    (locationState?.tab ?? "").toLowerCase().includes("laboratory");

  // Tabs depend on context; lab views only allow courier/admin roles.
  const availableTabs = useMemo<UserTabKey[]>(
    () => (isLaboratoryContext ? ["COURIERS", "ADMINS"] : ["NURSES", "SURGEONS", "ADMINS"]),
    [isLaboratoryContext]
  );

  const [selectedTab, setSelectedTab] = useState<UserTabKey>(
    () => (isLaboratoryContext ? "COURIERS" : "NURSES")
  );

  const nurseRoleId = useMemo(
    () =>
      rolesData?.items?.find((role: UserRole) => role?.name === "Nurse")
        ?.id,
    [rolesData]
  );

  const courierRoleId = useMemo(
    () =>
      rolesData?.items?.find((role: UserRole) => role?.name === "Courier")
        ?.id,
    [rolesData]
  );

  const healthOrgAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role: UserRole) => role?.name === "Health Organisation Admin"
      )?.id,
    [rolesData]
  );

  const laboratoryAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role: UserRole) => role?.name === "Laboratory Admin"
      )?.id,
    [rolesData]
  );

  useEffect(() => {
    // Keep selected tab valid when context changes (e.g., lab vs health org).
    if (!availableTabs.includes(selectedTab)) {
      setSelectedTab(availableTabs[0]);
    }
  }, [availableTabs, selectedTab]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: UserTabKey
  ) => {
    setSelectedTab(newValue);
  };

  // for searching in the query
  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKey(value === "" ? undefined : value);
  }, []);

  const labelText = USER_TAB_LABELS_SINGULAR[selectedTab] ?? "";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: colors.lightGrey,
          alignItems: "center",
          padding: "10px 15px",
        }}
        gap={2}
      >
        <Box width={"60%"} sx={{ backgroundColor: "primary.light" }}>
          <Tabs value={selectedTab} onChange={handleChange} variant="fullWidth">
            {availableTabs.map((tabKey) => (
              <Tab
                key={tabKey}
                value={tabKey}
                label={USER_TAB_LABELS[tabKey]}
              />
            ))}
          </Tabs>
        </Box>
        <MuiInput
          disableUnderline
          placeholder={`Search by name....`}
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchKey ?? ""}
          onChange={handleSearch}
          sx={{
            width: "300px",
            gap: "8px",
            marginBottom: 0,
            backgroundColor: colors.white,
            borderRadius: "50px",
          }}
        />
        <Button
          variant="contained"
          className={style.addButton}
          label={`Add New ${labelText}`}
          onClick={() => {
            // Route and state payload differ by role and context; keep ids aligned with target forms.
            const isCourierTab = selectedTab === "COURIERS";
            const roleId =
              selectedTab === "NURSES"
                ? nurseRoleId ?? DEFAULT_NURSE_ROLE_ID
                : selectedTab === "ADMINS"
                  ? isLaboratoryContext
                    ? laboratoryAdminRoleId ?? DEFAULT_LABORATORY_ADMIN_ROLE_ID
                    : healthOrgAdminRoleId ?? DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID
                  : isCourierTab
                    ? courierRoleId ?? DEFAULT_COURIER_ROLE_ID
                    : undefined;

            navigate("/user/add-tab", {
              state: {
                userType: selectedTab,
                userRole: roleId,
                laboratoryAdminRoleId:
                  laboratoryAdminRoleId ?? DEFAULT_LABORATORY_ADMIN_ROLE_ID,
                healthOrgId:
                  !isLaboratoryContext && id && !isCourierTab
                    ? Number(id)
                    : undefined,
                laboratoryId:
                  isLaboratoryContext || isCourierTab ? Number(id) : undefined,
              },
            });
          }}
          sx={{
            ":hover": { backgroundColor: colors.primary },
          }}
        />
      </Box>
      {selectedTab === "SURGEONS" && <SurgeonTab searchKey={searchKey} onClearSearch={() => setSearchKey(undefined)} />}
      {selectedTab === "NURSES" && <NurseTab searchKey={searchKey} onClearSearch={() => setSearchKey(undefined)} />}
      {selectedTab === "ADMINS" && (
        <AdminTab
          searchKey={searchKey}
          onClearSearch={() => setSearchKey(undefined)}
          scope={isLaboratoryContext ? "laboratory" : "healthOrg"}
        />
      )}
      {selectedTab === "COURIERS" && <CourierTab searchKey={searchKey} onClearSearch={() => setSearchKey(undefined)} />}
    </Box>
  );
};

export default UserList;
